import { CheckCircle2, Layers } from "lucide-react";
import DrawingReconciliation from "./DrawingReconciliation";

/**
 * Step2MaterialPanel — material selection card grid for the quote wizard.
 *
 * Extracted from NewQuotePage as part of the P1 refactor (the page had
 * grown past 650 lines). This component is purely presentational: state
 * lives in the parent and is passed through props.
 *
 * When the selected material is a sheet/lamiera (category starts with
 * "Lamiera"), an additional thickness input is shown. The value is sent to
 * `/quotes/analyze` so the backend forces `geometry_type='sheet'` and
 * prefers sheet-metal machines (press brake, shear, fiber laser, calandra).
 */
export default function Step2MaterialPanel({
  t,
  drawingReqs,
  geomFeatures,
  reconLoading,
  materials,
  materialId,
  setMaterialId,
  sheetThicknessMm,
  setSheetThicknessMm,
}) {
  const selectedMaterial = materials.find((m) => m.id === materialId);
  const isSheetMaterial = (selectedMaterial?.category || "").toLowerCase().startsWith("lamiera");

  return (
    <div data-testid="step-material">
      <div className="label-tech mb-2">{t("common.step")} 3</div>
      <h2 className="font-display font-bold text-xl text-slate-100 mb-1">{t("newQuote.step2Title")}</h2>
      <p className="text-slate-400 text-sm mb-6">{t("newQuote.step2Body")}</p>

      {(drawingReqs || reconLoading) && (
        <div className="mb-6">
          <DrawingReconciliation
            requirements={drawingReqs}
            features={geomFeatures}
            materials={materials}
            selectedMaterialId={materialId}
            onApplyMaterial={setMaterialId}
            loading={reconLoading}
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {materials.map((m) => {
          const sel = materialId === m.id;
          // Localize name/category if the material has an i18n entry, fallback to backend.
          const localName = t(`materials.${m.id}.name`, { defaultValue: m.name });
          const localCat = t(`materials.${m.id}.category`, { defaultValue: m.category });
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setMaterialId(m.id)}
              className={`text-left p-4 rounded-md border transition-all ${
                sel
                  ? "border-blue-500 bg-blue-500/10"
                  : "border-slate-700 hover:border-slate-500 bg-slate-900/40"
              }`}
              data-testid={`material-${m.id}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display font-semibold text-slate-100">{localName}</div>
                  <div className="text-xs text-slate-500 mt-1">{localCat}</div>
                </div>
                {sel && <CheckCircle2 size={18} className="text-blue-400" />}
              </div>
              <div className="mt-3 flex items-center gap-3 text-xs text-slate-400 font-mono-data">
                <span>ρ {m.density} g/cm³</span>
                <span>·</span>
                <span>€{m.price_per_kg}/kg</span>
                <span>·</span>
                <span>{m.machinability}x</span>
              </div>
            </button>
          );
        })}
      </div>

      {isSheetMaterial && setSheetThicknessMm && (
        <div className="mt-6 p-4 rounded-md border border-amber-500/40 bg-amber-500/5" data-testid="sheet-thickness-block">
          <div className="flex items-center gap-2 mb-2 text-amber-300">
            <Layers size={14}/>
            <div className="label-tech text-amber-300">{t("newQuote.sheetThickness")}</div>
          </div>
          <p className="text-xs text-slate-400 mb-3">
            {t("newQuote.sheetThicknessBody")}
          </p>
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.1"
              min="0.1"
              max="200"
              value={sheetThicknessMm || ""}
              onChange={(e) => setSheetThicknessMm(e.target.value)}
              placeholder="es. 2.0"
              className="w-32 px-3 py-2 rounded-md bg-slate-900 border border-slate-700 text-sm font-mono-data text-slate-100 focus:border-amber-500 focus:outline-none"
              data-testid="sheet-thickness-input"
            />
            <span className="text-sm text-slate-400">mm</span>
            <div className="ml-4 flex items-center gap-1.5">
              {[1, 1.5, 2, 3, 4, 6, 8, 10].map((t) => (
                <button
                  key={`thk-${t}`}
                  type="button"
                  onClick={() => setSheetThicknessMm(String(t))}
                  className={`px-2 py-1 rounded text-[11px] font-mono-data border transition-colors ${
                    Number(sheetThicknessMm) === t
                      ? "border-amber-500 bg-amber-500/20 text-amber-200"
                      : "border-slate-700 text-slate-400 hover:border-slate-500"
                  }`}
                  data-testid={`sheet-thickness-preset-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
